import { useMemo } from 'react'
import { findFeedbackForHost } from '@renderer/chat/integrations/feedback-hosting'
import { useStagedActionsQuery, type StagedAction } from '../../../hooks/use-staged-actions'
import { useStagedAction } from '../../../hooks/use-staged-action'
import { ActionViewFeedbackCard } from './feedback-card'

/**
 * The feedback question about the run that produced this report, floated above
 * the report's own controls instead of standing as its own row. HIG-5967.
 *
 * Found in the list cache but rendered from a hydrated fetch, which resolves
 * the conversation metadata the header needs. The list row itself does carry
 * `inputData` (`releaseAnswerableRun` builds a dismissal straight off it), so
 * do not trim it from the list payload.
 */
export function ActionViewHostedFeedback({ action }: { action: StagedAction | undefined }) {
  const { allActions } = useStagedActionsQuery()

  // Unfiltered: the visible list hides a hosted card, and drops it once
  // answered — either would pull this out from under the user.
  const siblingId = useMemo(
    () => (action ? findFeedbackForHost(action, allActions)?.id : undefined),
    [action, allActions],
  )

  const { action: feedback } = useStagedAction({ actionId: siblingId })

  if (!feedback) return null

  // Remount when the paired row changes, or the answered card's local state
  // renders the next question as already resolved.
  return <ActionViewFeedbackCard key={feedback.id} action={feedback} />
}
